/* Exercise Cards in the drill rotation (02 §2.2, 11 §4.6).
 *
 * The server returns the queue untruncated and leaves this presentation rule to
 * the client: no round of 20 carries more than two exercise Cards. Kept out of
 * the drill screen so it can be tested without a DOM.
 *
 * Rounds are returned EXPLICITLY rather than as one flat list, because they are
 * not always 20 long. 11 §4.6: when a tail contains only exercise Cards they
 * "flow into additional smaller rounds" — so a round boundary cannot be derived
 * from a card's position, and deriving it anyway silently violates the cap.
 */

import { DRILL_ROUND_SIZE, MAX_EXERCISE_CARDS_PER_ROUND } from '@ascent/shared';

export interface RotatableCard {
  id: string;
  exercise?: { type: string } | undefined;
}

/**
 * Split the queue into rounds, each holding at most two exercise Cards.
 *
 * Surplus exercise Cards are never dropped — they become extra, shorter rounds.
 * Dropping them would silently remove due work from the session.
 */
export function buildRounds<T extends RotatableCard>(cards: T[]): T[][] {
  const plain = cards.filter((c) => !c.exercise);
  const exercises = cards.filter((c) => c.exercise);
  if (exercises.length === 0) {
    const rounds: T[][] = [];
    for (let i = 0; i < plain.length; i += DRILL_ROUND_SIZE) {
      rounds.push(plain.slice(i, i + DRILL_ROUND_SIZE));
    }
    return rounds;
  }

  const rounds: T[][] = [];
  let p = 0;
  let e = 0;

  while (p < plain.length || e < exercises.length) {
    const taken: T[] = [];
    for (let k = 0; k < MAX_EXERCISE_CARDS_PER_ROUND && e < exercises.length; k++) {
      taken.push(exercises[e++]!);
    }
    const round: T[] = [];
    while (round.length + taken.length < DRILL_ROUND_SIZE && p < plain.length) {
      round.push(plain[p++]!);
    }
    if (round.length === 0 && taken.length === 0) break;

    // Seat them a third and two thirds in rather than first: an exercise is a
    // gear change, and both of them up front makes a round feel like homework.
    const seats = [Math.floor(round.length / 3), Math.floor((round.length * 2) / 3) + 1];
    taken.forEach((c, i) => round.splice(Math.min(seats[i] ?? round.length, round.length), 0, c));
    rounds.push(round);
  }
  return rounds;
}

/** Flat playback order plus the index each round starts at. */
export function planRounds<T extends RotatableCard>(cards: T[]): { cards: T[]; starts: number[] } {
  const rounds = buildRounds(cards);
  const starts: number[] = [];
  const flat: T[] = [];
  for (const round of rounds) {
    starts.push(flat.length);
    flat.push(...round);
  }
  return { cards: flat, starts };
}

/** 1-based round number containing a given playback index. */
export function roundOf(starts: number[], index: number): number {
  let n = 1;
  for (let i = 0; i < starts.length; i++) {
    if (index >= starts[i]!) n = i + 1;
  }
  return n;
}

/**
 * The offline substitution rule (02 §6.10).
 *
 * Offline with no pack, a `python` Card is dropped from THIS session and nothing
 * is sent for it, so the server reschedules it exactly as if it had never been
 * shown. Recording a fake grade, or showing a Card the learner cannot answer,
 * would both corrupt the schedule. `sql` and `reading` need no pack and stay.
 */
export function playableCards<T extends RotatableCard>(cards: T[], pythonRunnable: boolean): T[] {
  return pythonRunnable ? cards : cards.filter((c) => c.exercise?.type !== 'python');
}
